import { OutreachForm } from "@components/campaigns/stages/OutreachForm";
import { OutreachTimeline } from "@components/campaigns/stages/OutreachTimeline";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { CopyButton } from "@components/shared/CopyButton";
import { OriginBadge } from "@components/shared/OriginBadge";
import {
  CREATE_OUTREACH_ATTEMPT,
  UPDATE_CANDIDATE_OUTREACH_MESSAGE,
  UPDATE_OUTREACH_ATTEMPT,
} from "@lib/graphql/mutations";
import { AlertTriangle, Loader2, Send, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "urql";
import { OutreachDisclaimer } from "./OutreachDisclaimer";
import {
  CHANNELS,
  type ChannelSpec,
  type DraftEntry,
  type DraftKind,
  draftFor,
  draftState,
  draftStateLabel,
  draftText,
  type WorkspaceCandidate,
} from "./outreachDrafts";

const LINKEDIN_NOTE_HARD_LIMIT = 300;
const LINKEDIN_NOTE_TARGET_LIMIT = 250;
const EMAIL_LABELS: DraftEntry[] = [
  { label: "Subject", body: "" },
  { label: "Body", body: "" },
];
const CONNECT_LABELS: DraftEntry[] = [{ label: "Connection request", body: "" }];

interface OutreachDraftEditorProps {
  candidate: WorkspaceCandidate;
  channel: DraftKind;
  onChannelChange: (kind: DraftKind) => void;
  onGenerate: (kind: DraftKind) => void;
  onChanged: () => void;
}

/** Right pane of the Outreach workspace: one candidate's drafts, a tab per
 *  channel, and the honest bridge to the contact log ("Mark as sent") (#3169). */
export function OutreachDraftEditor({
  candidate,
  channel,
  onChannelChange,
  onGenerate,
  onChanged,
}: OutreachDraftEditorProps) {
  const spec = CHANNELS.find((c) => c.kind === channel) ?? CHANNELS[0];
  const draft = draftFor(candidate, spec);
  const version = draft?.currentVersion ?? null;
  const state = draftState(draft);

  const [entries, setEntries] = useState<DraftEntry[]>(version?.messages ?? []);
  const lastVersionIdRef = useRef<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [logging, setLogging] = useState(false);

  const [{ fetching: saving }, updateDraft] = useMutation(UPDATE_CANDIDATE_OUTREACH_MESSAGE);
  const [{ fetching: loggingSave }, createAttempt] = useMutation(CREATE_OUTREACH_ATTEMPT);
  const [, updateAttempt] = useMutation(UPDATE_OUTREACH_ATTEMPT);

  // Reset the editable copy when the candidate, channel or version changes.
  useEffect(() => {
    const key = version ? `${candidate.id}:${spec.kind}:${version.id}` : null;
    if (key === lastVersionIdRef.current) return;
    lastVersionIdRef.current = key;
    setEntries(version?.messages ?? []);
    setLocalError(null);
    setLogging(false);
  }, [candidate.id, spec.kind, version]);

  const original = version?.messages ?? [];
  const dirty =
    entries.length !== original.length ||
    entries.some((e, i) => e.body !== (original[i]?.body ?? "") || e.label !== original[i]?.label);

  const setBody = (index: number, body: string) =>
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, body } : e)));

  const handleSave = async () => {
    if (!draft) return;
    setLocalError(null);
    const result = await updateDraft({
      input: { id: draft.id, messages: entries.map((e) => ({ label: e.label, body: e.body })) },
    });
    const errors = result.data?.updateCandidateOutreachMessage?.errors ?? [];
    if (errors.length) {
      setLocalError(errors.join("; "));
      return;
    }
    onChanged();
  };

  const handleLogContact = async (data: {
    channel: string;
    direction: string;
    status: string;
    messageContent: string;
  }) => {
    const input: Record<string, string> = {
      campaignCandidateId: candidate.id,
      channel: data.channel,
      direction: data.direction,
      status: data.status,
      sentAt: new Date().toISOString(),
    };
    if (data.messageContent) input.messageContent = data.messageContent;
    const result = await createAttempt({ input });
    const errors = result.data?.createOutreachAttempt?.errors ?? [];
    if (errors.length) {
      setLocalError(errors.join("; "));
      return;
    }
    setLogging(false);
    onChanged();
  };

  const handleUpdateContact = async (
    attemptId: string,
    data: { status?: string; responseContent?: string; respondedAt?: string },
  ) => {
    await updateAttempt({ input: { id: attemptId, ...data } });
    onChanged();
  };

  const name = candidate.candidate.fullName;
  const subtitle = [candidate.candidate.currentRoleTitle, candidate.candidate.currentCompany]
    .filter(Boolean)
    .join(" at ");
  const hasDraftText = version !== null && state !== "generating" && entries.length > 0;
  const fullText = draftText(spec.kind, entries);

  return (
    <div className="flex h-full flex-col rounded-lg bg-card shadow-sm ring-1 ring-ring">
      <div className="border-b border-border px-4 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-text-primary">
              {name}
              {typeof candidate.candidate.referenceNumber === "number" && (
                <CandidateReferenceBadge
                  referenceNumber={candidate.candidate.referenceNumber}
                  variant="detail"
                />
              )}
            </h2>
            {subtitle && <p className="text-xs text-text-muted">{subtitle}</p>}
          </div>
        </div>
        <div role="tablist" aria-label="Draft channel" className="mt-3 flex gap-1">
          {CHANNELS.map((c: ChannelSpec) => {
            const active = c.kind === spec.kind;
            return (
              <button
                key={c.kind}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChannelChange(c.kind)}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium ${
                  active
                    ? "border-accent text-text-primary"
                    : "border-transparent text-text-muted hover:text-text-secondary"
                }`}
              >
                {c.label}
                <span className="rounded-full bg-text-muted/10 px-1.5 py-0.5 text-[10px] text-text-muted">
                  {draftStateLabel(draftFor(candidate, c))}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 p-4">
        <OutreachDisclaimer />

        {version && (
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-text-faint">
            <span>v{version.versionNumber}</span>
            <OriginBadge origin={version.origin} actorSource={null} />
            {version.changeSummary && <span>· {version.changeSummary}</span>}
          </div>
        )}

        {state === "error" && version?.generationError && (
          <div className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{version.generationError}</span>
          </div>
        )}
        {localError && (
          <div
            data-testid="editor-error"
            className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
          >
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{localError}</span>
          </div>
        )}

        {state === "none" && (
          <div className="flex items-center justify-between gap-3 rounded border border-dashed border-border p-4">
            <p className="text-xs text-text-muted">
              No {spec.label.toLowerCase()} draft yet for {name}.
            </p>
            <button
              type="button"
              onClick={() => onGenerate(spec.kind)}
              className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90"
            >
              <Sparkles size={12} /> Generate
            </button>
          </div>
        )}

        {state === "generating" && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <Loader2 size={14} className="animate-spin" /> Drafting {spec.label.toLowerCase()}…
          </div>
        )}

        {hasDraftText && spec.kind === "email" && (
          <EmailFields
            name={name}
            email={candidate.candidate.email}
            entries={entries.length === 2 ? entries : EMAIL_LABELS}
            onChange={setBody}
          />
        )}
        {hasDraftText && spec.kind === "linkedin_message" && (
          <LabelledFields name={name} entries={entries} rows={6} onChange={setBody} />
        )}
        {hasDraftText && spec.kind === "linkedin_connection_request" && (
          <ConnectionFields
            name={name}
            entries={entries.length === 1 ? entries : CONNECT_LABELS}
            onChange={setBody}
          />
        )}

        {hasDraftText && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CopyButton
                ariaLabel="Copy draft"
                getContent={() => ({ text: fullText, html: fullText })}
              />
              <span className="text-[10px] text-text-faint">Copy the whole draft</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setLogging((v) => !v)}
                className="flex items-center gap-1 rounded border border-border px-3 py-1 text-xs text-text-secondary hover:bg-card-hover"
              >
                <Send size={12} /> Mark as sent…
              </button>
              <button
                type="button"
                onClick={() => onGenerate(spec.kind)}
                className="rounded px-3 py-1 text-xs text-text-muted hover:bg-card-hover hover:text-text-secondary"
              >
                Regenerate
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                Save
              </button>
            </div>
          </div>
        )}

        {logging && (
          <OutreachForm
            onSave={handleLogContact}
            onCancel={() => setLogging(false)}
            saving={loggingSave}
            initial={{
              channel: spec.logChannel,
              direction: "outbound",
              status: "sent",
              messageContent: fullText,
            }}
          />
        )}

        <div className="border-t border-border pt-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Contact log ({candidate.outreachAttempts.length})
          </h3>
          <OutreachTimeline attempts={candidate.outreachAttempts} onUpdate={handleUpdateContact} />
        </div>
      </div>
    </div>
  );
}

function EmailFields({
  name,
  email,
  entries,
  onChange,
}: {
  name: string;
  email: string | null;
  entries: DraftEntry[];
  onChange: (index: number, body: string) => void;
}) {
  const subject = entries[0]?.body ?? "";
  const body = entries[1]?.body ?? "";
  return (
    <div className="space-y-2 rounded border border-border">
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
        <span className="w-14 text-text-faint">To</span>
        <span className="text-text-secondary">
          {name}
          {email ? ` <${email}>` : " — no email on file"}
        </span>
      </div>
      <div className="flex items-center gap-2 border-b border-border px-3 py-1.5 text-xs">
        <label htmlFor="outreach-email-subject" className="w-14 text-text-faint">
          Subject
        </label>
        <input
          id="outreach-email-subject"
          type="text"
          aria-label={`${name} – Subject`}
          value={subject}
          onChange={(e) => onChange(0, e.target.value)}
          className="flex-1 bg-transparent text-text-primary focus:outline-none"
        />
        <CopyButton
          ariaLabel="Copy Subject"
          getContent={() => ({ text: subject, html: subject })}
        />
      </div>
      <div className="px-3 pb-3">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wide text-text-faint">Body</span>
          <CopyButton ariaLabel="Copy Body" getContent={() => ({ text: body, html: body })} />
        </div>
        <textarea
          aria-label={`${name} – Body`}
          value={body}
          onChange={(e) => onChange(1, e.target.value)}
          rows={12}
          className="w-full resize-y rounded border border-border bg-background p-2 text-xs text-text-primary focus:border-accent focus:outline-none"
        />
      </div>
    </div>
  );
}

function LabelledFields({
  name,
  entries,
  rows,
  onChange,
}: {
  name: string;
  entries: DraftEntry[];
  rows: number;
  onChange: (index: number, body: string) => void;
}) {
  return (
    <div className="space-y-3">
      {entries.map((entry, i) => (
        <div key={entry.label} className="rounded border border-border p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-text-secondary">{entry.label}</span>
            <CopyButton
              ariaLabel={`Copy ${entry.label}`}
              getContent={() => ({ text: entry.body, html: entry.body })}
            />
          </div>
          <textarea
            aria-label={`${name} – ${entry.label}`}
            value={entry.body}
            onChange={(e) => onChange(i, e.target.value)}
            rows={rows}
            className="w-full resize-y rounded border border-border bg-background p-2 text-xs text-text-primary focus:border-accent focus:outline-none"
          />
        </div>
      ))}
    </div>
  );
}

function ConnectionFields({
  name,
  entries,
  onChange,
}: {
  name: string;
  entries: DraftEntry[];
  onChange: (index: number, body: string) => void;
}) {
  const body = entries[0]?.body ?? "";
  const overLimit = body.length > LINKEDIN_NOTE_HARD_LIMIT;
  const nearing = !overLimit && body.length > LINKEDIN_NOTE_TARGET_LIMIT;
  return (
    <div className="rounded border border-border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-text-secondary">Connection request</span>
        <CopyButton
          ariaLabel="Copy Connection request"
          getContent={() => ({ text: body, html: body })}
        />
      </div>
      <textarea
        aria-label={`${name} – Connection request`}
        value={body}
        onChange={(e) => onChange(0, e.target.value)}
        rows={4}
        className="w-full resize-y rounded border border-border bg-background p-2 text-xs text-text-primary focus:border-accent focus:outline-none"
      />
      <p
        data-testid="char-counter"
        className={`mt-1 text-[10px] ${
          overLimit ? "text-destructive" : nearing ? "text-amber-600" : "text-text-faint"
        }`}
      >
        {body.length} / {LINKEDIN_NOTE_HARD_LIMIT} characters
        {overLimit && " — LinkedIn will reject this"}
      </p>
    </div>
  );
}
